import { Component, OnInit } from '@angular/core';
import moment from 'moment';
import { MessageService } from 'primeng/api';
import { AppDataService, MerchantService } from 'app/services';
import { Utils } from 'app/utils';
import { MenuHomeEnum } from 'app/components/home';
import { MenuPermissionConfig, MenuPermissionActionEnum } from 'app/models';

export enum PartnerSettingsMenuLevel1Enum {
    BusinessInfo = 1
}

export enum PartnerSettingsMenuLevel2Enum {
    None = 0,
    LegalDocuments = 1,
    PayoutAccount = 2,
    AuthorizedRepresentative = 3,
    PartnershipTerms = 4
}

export enum PartnerSettingsMenuLevel3Enum {
    None = 0,

    //Legal Documents
    SECDocument = 1,
    BIR2303 = 2,
    BusinessPermit = 3
}

@Component({
    selector: 'partner-settings-root',
    templateUrl: './partner-settings-root.component.html',
    styleUrls: ['./partner-settings-root.component.scss']
})

export class PartnerSettingsRootComponent implements OnInit {
    public menuEnum = MenuHomeEnum;
    public activeMenu: MenuHomeEnum = MenuHomeEnum.Marketing;

    public menuPermissionConfig = MenuPermissionConfig;
    public menuPermissionActionEnum = MenuPermissionActionEnum;

    public menuPermissionActive!: string;

    constructor(private messageService: MessageService,
        private merchantService: MerchantService,
        private appData: AppDataService) { }

    ngOnInit(): void {
        setTimeout(() => {
            this.getBusinessInfo();
            this.getMerchantDocs();
        }, 1000);
    }

    public businessInfo: any;
    public legalDocs: any;
    public loading: boolean = false;

    public menuLevel1Enum = PartnerSettingsMenuLevel1Enum;
    public activeMenuLevel1Item: PartnerSettingsMenuLevel1Enum = PartnerSettingsMenuLevel1Enum.BusinessInfo;

    public menuLevel2Enum = PartnerSettingsMenuLevel2Enum;
    public activeMenuLevel2Item: PartnerSettingsMenuLevel2Enum = PartnerSettingsMenuLevel2Enum.None;

    public menuLevel3Enum = PartnerSettingsMenuLevel3Enum;
    public activeMenuLevel3Item: PartnerSettingsMenuLevel3Enum = PartnerSettingsMenuLevel3Enum.None;

    public showLegalDocuments: boolean = false;
    public showPayoutAccount: boolean = false;
    public showAuthorizedRepresentative: boolean = false;
    public showPartnershipTerms: boolean = false;

    public clickMenu(menuPermissionActive: string, menu: MenuHomeEnum) {
        this.menuPermissionActive = menuPermissionActive;
        this.activeMenu = menu;
    }

    public onMenuLevel1Click(menuItem: PartnerSettingsMenuLevel1Enum) {
        this.activeMenuLevel1Item = menuItem;

        // Clear level2 menu
        this.activeMenuLevel2Item = PartnerSettingsMenuLevel2Enum.None;

        this.showLegalDocuments = false;
        this.showPayoutAccount = false;
        this.showAuthorizedRepresentative = false;
        this.showPartnershipTerms = false;
    }

    public onMenuLevel2Click(menuItem: PartnerSettingsMenuLevel2Enum) {
        this.activeMenuLevel2Item = menuItem;

        this.updateMenuLevel2();

        if (menuItem === PartnerSettingsMenuLevel2Enum.LegalDocuments) {
            this.onMenuLevel3Click(PartnerSettingsMenuLevel3Enum.SECDocument);
        }
    }

    private updateMenuLevel2(): void {
        this.showLegalDocuments = this.activeMenuLevel2Item === PartnerSettingsMenuLevel2Enum.LegalDocuments;
        this.showPayoutAccount = this.activeMenuLevel2Item === PartnerSettingsMenuLevel2Enum.PayoutAccount;
        this.showAuthorizedRepresentative = this.activeMenuLevel2Item === PartnerSettingsMenuLevel2Enum.AuthorizedRepresentative;
        this.showPartnershipTerms = this.activeMenuLevel2Item === PartnerSettingsMenuLevel2Enum.PartnershipTerms;
    }

    public onMenuLevel3Click(menuItem: PartnerSettingsMenuLevel3Enum) {
        this.activeMenuLevel3Item = menuItem;
    }

    public getBusinessInfo() {
        this.loading = true;

        const dto = {
            MerchantId: this.appData.authData.MerchantId
        };

        this.merchantService.detailMerchant(dto, this.messageService).then(data => {
            if (!data) {
                this.loading = false;
                return;
            }

            if (!data.Success) {
                this.loading = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                return;
            }
            this.loading = false;
            this.businessInfo = data;
            this.businessInfo.Birthday = Utils.utcToLocaleDateString(this.businessInfo.Birthday);
            this.businessInfo.CompanyAuthorizationLetterExpirationDate = Utils.utcToLocaleDateString(this.businessInfo.CompanyAuthorizationLetterExpirationDate);
            this.businessInfo.CompanyAuthorizationLetterIssueDate = Utils.utcToLocaleDateString(this.businessInfo.CompanyAuthorizationLetterIssueDate);
            this.businessInfo.PersonalIdentificationExpirationDate = Utils.utcToLocaleDateString(this.businessInfo.PersonalIdentificationExpirationDate);
            this.businessInfo.PersonalIdentificationIssueDate = Utils.utcToLocaleDateString(this.businessInfo.PersonalIdentificationIssueDate);
            this.businessInfo.SubmittedAt = this.businessInfo.SubmittedAt ? moment(new Date(this.businessInfo.SubmittedAt)).format('DD MMMM YYYY') : '-';
            this.businessInfo.DateActivatedAt = this.businessInfo.DateActivatedAt ? moment(new Date(this.businessInfo.DateActivatedAt)).format('DD MMMM YYYY') : '-';
        });
    }

    public getMerchantDocs() {
        const dto = {
            MerchantId: this.appData.authData.MerchantId
        };
        this.loading = true;
        this.merchantService.getMerchantDocuments(dto, this.messageService).then(data => {
            if (!data) {
                this.loading = false;
                return;
            }

            if (!data.Success) {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                this.loading = false;
                return;
            }
            this.legalDocs = data;

            this.legalDocs.SECDocumentIssueDate = Utils.utcToLocaleDateString(this.legalDocs.SECDocumentIssueDate);
            this.legalDocs.BIR2303IssueDate = Utils.utcToLocaleDateString(this.legalDocs.BIR2303IssueDate);
            this.legalDocs.BIR2303ExpirationDate = Utils.utcToLocaleDateString(this.legalDocs.BIR2303ExpirationDate);
            this.legalDocs.BusinessPermitIssueDate = Utils.utcToLocaleDateString(this.legalDocs.BusinessPermitIssueDate);
            this.legalDocs.BusinessPermitExpirationDate = Utils.utcToLocaleDateString(this.legalDocs.BusinessPermitExpirationDate);

            this.loading = false;
        }).catch(err => {
            this.loading = false;
            throw err;
        });
    }

}
